import React from 'react';
import {
  Bullseye,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  Spinner,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, SyncAltIcon, UsersIcon } from '@patternfly/react-icons';
import { countries } from 'countries-list';
import type { DomainName } from '../../../types/models/DomainName';
import { useDomainContact } from '../../../hooks/useDomainContact';
import { formatDateTime } from '../../../utils/dateFormat';

interface DomainContactsTabProps {
  domainName: DomainName;
}

/**
 * Resolve an ISO 3166-1 alpha-2 code to "Country name (CC)", falling back to the raw code when it
 * is unknown. Country names come from the `countries-list` package (the project's single source of
 * truth — keyed by upper-cased alpha-2, matching the contact's stored `countrycode`).
 */
function formatCountry(code: string): string {
  const name = (countries as Record<string, { name: string }>)[code]?.name;
  return name ? `${name} (${code})` : code;
}

function DomainContactsTab({ domainName }: DomainContactsTabProps) {
  // The registrant contact is its own sub-resource (GET …/{id}/contact), not part of the domain
  // object, so it is fetched here. The parent Tabs use mountOnEnter, so this fires only when the
  // Contacts tab is first opened; the Refresh action re-runs it.
  const { contact, loading, error, refetch } = useDomainContact(domainName.id ?? 0);

  const renderBody = () => {
    if (loading) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading registrant contact" />
        </Bullseye>
      );
    }

    if (error) {
      return (
        <EmptyState
          titleText="Couldn't load the registrant contact"
          icon={ExclamationCircleIcon}
          variant="sm"
          headingLevel="h3"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="link" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (contact === null) {
      return (
        <EmptyState titleText="No registrant contact" icon={UsersIcon} variant="sm" headingLevel="h3">
          <EmptyStateBody>
            No registrant (WHOIS owner) contact has been recorded for {domainName.name} yet.
          </EmptyStateBody>
        </EmptyState>
      );
    }

    return (
      <DescriptionList columnModifier={{ default: '1Col', sm: '2Col' }}>
        <DescriptionListGroup>
          <DescriptionListTerm>First name</DescriptionListTerm>
          <DescriptionListDescription>{contact.first_name}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Last name</DescriptionListTerm>
          <DescriptionListDescription>{contact.last_name}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Organization</DescriptionListTerm>
          <DescriptionListDescription>{contact.organization || '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Email</DescriptionListTerm>
          <DescriptionListDescription>{contact.email}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Phone</DescriptionListTerm>
          <DescriptionListDescription>{contact.phone}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Address line 1</DescriptionListTerm>
          <DescriptionListDescription>{contact.address_line1}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Address line 2</DescriptionListTerm>
          <DescriptionListDescription>{contact.address_line2 || '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>City</DescriptionListTerm>
          <DescriptionListDescription>{contact.city}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>State / Province</DescriptionListTerm>
          <DescriptionListDescription>{contact.state || '—'}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Postal code</DescriptionListTerm>
          <DescriptionListDescription>{contact.zipcode}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Country</DescriptionListTerm>
          <DescriptionListDescription>{formatCountry(contact.countrycode)}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>WHOIS privacy</DescriptionListTerm>
          <DescriptionListDescription>
            {contact.privacy_enabled ? (
              <Label color="blue" isCompact>
                Enabled
              </Label>
            ) : (
              <Label color="grey" isCompact>
                Disabled
              </Label>
            )}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Created</DescriptionListTerm>
          <DescriptionListDescription>{formatDateTime(contact.created_at)}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Last updated</DescriptionListTerm>
          <DescriptionListDescription>{formatDateTime(contact.updated_at)}</DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
    );
  };

  return (
    <Card>
      <CardHeader
        actions={{
          actions: (
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              isLoading={loading}
              spinnerAriaValueText="Refreshing registrant contact"
              onClick={() => {
                if (!loading) {
                  void refetch();
                }
              }}
            >
              Refresh
            </Button>
          ),
        }}
      >
        <CardTitle>Registrant contact</CardTitle>
      </CardHeader>
      <CardBody>{renderBody()}</CardBody>
    </Card>
  );
}

export default DomainContactsTab;
